import { useAuth0 } from "@auth0/auth0-react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Box, Divider } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { LinkItem } from "LinkItem";
import { OLButton } from "OLButton";
import { getErrorMessage } from "api/errors";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { loginRequest } from "authConfig";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { I18nContext } from "contexts/I18n/I18nContext";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useAuth } from "hooks/auth";
import { useMessage } from "hooks/intl";
import { useContext, useEffect } from "react";
import {
  selectLogoutReason,
  setLogoutReason,
} from "reduxState/logoutReason/logoutReasonSlice";
import { getEnvVariable } from "utilities/env";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { OKTALoginButton } from "./OKTALoginButton";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    paper: {
      backgroundColor: theme.palette.background.paper,
      border: "none",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      maxWidth: "95%",
      padding: theme.spacing(12, 12, 16),
    },
    header: {
      color: theme.black,
      fontSize: 20,
      fontWeight: 500,
      lineHeight: "32px",
      marginBottom: theme.spacing(6),
    },
    Input: {
      maxWidth: "100%",
      width: 406,
      "& + &": {
        marginTop: theme.spacing(12),
      },
    },
    submit: {
      marginTop: theme.spacing(12),
    },
    checkbox: {
      color: theme.gray500,
      marginTop: theme.spacing(12),
    },
    form: {
      display: "flex",
      flexDirection: "column",
    },
    idleError: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      maxWidth: 406,
      marginBottom: theme.spacing(15),
      marginTop: theme.spacing(-5),
    },
    error: {
      color: theme.red500,
      display: "block",
      fontSize: 14,
      marginBottom: theme.spacing(8),
      marginTop: theme.spacing(5),
      maxWidth: 406,
      "& + &": { marginTop: 0 },
    },
    link: {
      textAlign: "center",
      textTransform: "uppercase",
      fontWeight: 500,
      fontSize: 15,
    },
    authLink: {
      marginTop: theme.spacing(8),
    },
    orContainer: {
      display: "flex",
      alignItems: "center",
      margin: theme.spacing(16, 0, 4),
      "& hr": {
        flexGrow: 1,
      },
      "& span": {
        padding: theme.spacing(0, 12),
        fontWeight: 500,
        fontSize: 16,
      },
    },
  }),
);

const domain = getEnvVariable("REACT_APP_AUTH0_DOMAIN");
const clientId = getEnvVariable("REACT_APP_AUTH0_CLIENT_ID");

const MAClientId = getEnvVariable("REACT_APP_MA_CLIENT_ID");
const MADirectoryId = getEnvVariable("REACT_APP_MA_PROVIDER_APP_URL");

const OKTAClientId = getEnvVariable("REACT_APP_OKTA_CLIENT_ID");
const OKTADirectoryId = getEnvVariable("REACT_APP_OKTA_PROVIDER_APP_URL");

export function LoginForm() {
  const classes = useStyles();
  const { message } = useMessage();
  const { login, loginWithAuth0, loginWithMA } = useAuth();
  const { notify } = useNotifications();
  const {
    loginWithRedirect,
    getIdTokenClaims,
    isAuthenticated: isAuthenticatedWithAuth0,
  } = useAuth0();
  const { refetchLanguages } = useContext(I18nContext);
  const dispatch = useAppDispatch();
  const { logoutReason } = useAppSelector(selectLogoutReason);
  const { instance, accounts } = useMsal();
  const isAuthenticatedWithMA = useIsAuthenticated();
  // Clear logout reason
  useEffect(() => {
    return () => {
      dispatch(setLogoutReason(""));
    };
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticatedWithAuth0) {
      const login = async () => {
        try {
          const result = await getIdTokenClaims();

          if (result) {
            await loginWithAuth0(result.__raw);
          }

          notify(message({ id: "loginSuccessful" }));
        } catch (error) {
          message({ id: "unknownErrorDuringLogin" });
        }
      };

      login();
    }
  }, [
    getIdTokenClaims,
    isAuthenticatedWithAuth0,
    loginWithAuth0,
    message,
    notify,
  ]);
  useEffect(() => {
    if (isAuthenticatedWithMA) {
      const login = async () => {
        try {
          instance
            .acquireTokenSilent({
              ...loginRequest,
              account: accounts[0],
            })
            .then((response) => {
              loginWithMA(response.accessToken);
            })
            .then(() => {
              notify(message({ id: "loginSuccessful" }));
            });
        } catch (error) {
          message({ id: "unknownErrorDuringLogin" });
        }
      };

      login();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticatedWithMA]);

  refetchLanguages();

  const isIdleLogout = logoutReason === "idle";

  const isMALoginButtonEnabled = MAClientId && MADirectoryId;
  const isOKTALoginButtonEnabled = OKTAClientId && OKTADirectoryId;

  return (
    <>
      <div className={classes.paper}>
        <h1 className={classes.header}>
          {message({ id: "loginToYourAccount" })}
        </h1>
        {isIdleLogout && (
          <span className={classes.idleError}>
            {message({ id: "automaticLogout" })}
          </span>
        )}
        <Formik
          initialStatus={{}}
          initialValues={{ username: "", password: "", keepSigned: false }}
          onSubmit={async (values, { setSubmitting, setStatus }) => {
            setSubmitting(true);
            setStatus({});
            try {
              await login(values.username, values.password, values.keepSigned);
              notify(message({ id: "loginSuccessful" }));
            } catch (error) {
              if (getErrorMessage(error)?.includes("Bad credentials")) {
                setSubmitting(false);
                setStatus({ "": message({ id: "badCredentials" }) });
                focusError();
              } else if (
                getErrorMessage(error)?.includes(
                  "Too many failed login attempts",
                )
              ) {
                setSubmitting(false);
                setStatus({
                  "": message({ id: "tooManyFailedLoginAttempts" }),
                });
                focusError();
              } else {
                notify(message({ id: "unknownErrorDuringLogin" }));
              }
            }
          }}
        >
          {({ handleChange, handleSubmit, isSubmitting, status }) => {
            return (
              <Form onSubmit={handleSubmit} className={classes.form}>
                {status?.[""] && (
                  <span className={classes.error}>{status[""]}</span>
                )}

                <TextInput
                  className={classes.Input}
                  name="username"
                  data-test-id="login-email"
                  label={
                    <RequiredFieldLabel
                      text={message({ id: "emailAddress" })}
                    />
                  }
                  placeholder={message({ id: "emailAddress" })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <TextInput
                  className={classes.Input}
                  name="password"
                  type="password"
                  data-test-id="login-password"
                  label={
                    <RequiredFieldLabel text={message({ id: "password" })} />
                  }
                  placeholder={message({ id: "password" })}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <FormControlLabel
                  className={classes.checkbox}
                  control={
                    <Checkbox
                      color="primary"
                      name="keepSigned"
                      onChange={handleChange}
                    />
                  }
                  label={message({ id: "keepMeLoggedIn" })}
                  data-test-id="login-keep-me-logged"
                />
                <OLButton
                  className={classes.submit}
                  color="primary"
                  disabled={isSubmitting}
                  fullWidth
                  type="submit"
                  variant="contained"
                  data-test-id="login-submit"
                  size="large"
                >
                  {message({ id: "login" })}
                </OLButton>
              </Form>
            );
          }}
        </Formik>
        <LinkItem
          blue
          to={links.passwordRecovery}
          data-test-id="login-forgot-password"
          className={classes.link}
        >
          {message({ id: "forgotPassword" })}
        </LinkItem>
        {domain && clientId && (
          <OLButton
            className={classes.authLink}
            color="primary"
            fullWidth
            type="submit"
            variant="outlined"
            onClick={() => {
              loginWithRedirect();
            }}
            size="large"
          >
            {message({ id: "continueWithAuth0" })}
          </OLButton>
        )}
        {(isMALoginButtonEnabled || isOKTALoginButtonEnabled) && (
          <Box className={classes.orContainer}>
            <Divider />
            <span>{message({ id: "or" }).toLocaleUpperCase()}</span>
            <Divider />
          </Box>
        )}
        {isMALoginButtonEnabled && (
          <OLButton
            className={classes.authLink}
            color="primary"
            fullWidth
            type="submit"
            variant="outlined"
            onClick={() =>
              instance.loginRedirect(loginRequest).catch((e) => {
                console.log(e);
              })
            }
            size="large"
          >
            {message({ id: "continueWithSSO" })}
          </OLButton>
        )}
        {isOKTALoginButtonEnabled && <OKTALoginButton />}
      </div>
    </>
  );
}
